import { clsx } from 'clsx';
import * as React from 'react';
import Image from '~/components/Image';
import DEFAULT_AVATAR from '../public/static/missing-avatar.png';

type BaseAvatarUrlProps = Omit<React.ComponentProps<typeof Image>, 'src'> & {
  size?: 'small' | 'medium' | 'large' | 'xl' | '2xl' | 'unspecified';
};

type AvatarPropsNoAvatarUrl = BaseAvatarUrlProps & {
  alt?: string;
  avatarUrl?: null;
};

type AvatarPropsWithAvatarUrl = BaseAvatarUrlProps & {
  alt: string | undefined; // Allow undefined but not missing
  avatarUrl: string;
};

type AvatarProps = AvatarPropsNoAvatarUrl | AvatarPropsWithAvatarUrl;

function sizeToDimensions(size: AvatarProps['size']) {
  switch (size) {
    case 'small':
      return {
        className: 'h-6 w-6',
        height: 24,
        width: 24,
      };
    case 'medium':
      return {
        className: 'h-8 w-8',
        height: 32,
        width: 32,
      };
    case 'large':
      return {
        className: 'h-10 w-10',
        height: 40,
        width: 40,
      };
    case 'xl':
      return {
        className: 'h-14 w-14',
        height: 56,
        width: 56,
      };
    case '2xl':
      return {
        className: 'h-24 w-24',
        height: 96,
        width: 96,
      };
    case 'unspecified':
      return {
        className: 'h-28 w-28',
        height: 112,
        width: 112,
      };
    default:
      return {
        className: 'h-10 w-10',
        height: 40,
        width: 40,
      };
  }
}

export const Avatar: React.FC<AvatarProps> = ({
  avatarUrl,
  className,
  size,
  ...props
}) => {
  const { className: sizeClassName, height, width } = sizeToDimensions(size);

  return (
    <Image
      className={clsx(
        'border-grey-200 max-w-none flex-shrink-0 rounded-full border-2 object-cover',
        sizeClassName,
        className,
      )}
      {...props}
      height={height}
      width={width}
      src={avatarUrl || DEFAULT_AVATAR}
    />
  );
};
